// Canvas 4: Value and Investment Case (workbook tab 04 Value Case).
// Steps: copy top pick, journey and sponsor KPIs (a pull block); four value rows; the money line; the investment side and
// contingency; the Two-Story Test; the value story. Money is typed as whole amounts and shown in millions with moneyM.
import { makeDef, G, text, long, num, pick, tag, calc, fields, table, pull, reference } from './base.js';
import { OPT } from '../spine.js';
import { S, N, T, roundHalfUp, fix, thousands, moneyM, gap } from '../helpers.js';

// ---------------------------------------------------------------- constants
const KPI_NONE = 'None the sponsor owns yet: flag for the CFO';

const ROWS = [
  { id: 'customer', label: 'Customer' },
  { id: 'business', label: 'Business', sub: 'the growth story' },
  { id: 'efficiency', label: 'AI and efficiency', sub: 'the efficiency story' },
  { id: 'trust', label: 'Trust and Human', sub: 'a guardrail measure' },
];
const ROW_IDS = ROWS.map((r) => r.id);
const ROW_NAMES = ROWS.map((r) => r.label);

const INV_ROWS = [
  { id: 'oneoff', label: 'One-off cost', sub: 'build, integrate, licence' },
  { id: 'p1', label: 'Phase 1 tranche', sub: 'the part of the one-off cost released first' },
  { id: 'p2', label: 'Phase 2 tranche', sub: 'the rest of the one-off cost, released later' },
  { id: 'run', label: 'Run cost per year' },
  { id: 'guard', label: 'Guardrail and change cost', sub: 'return after Canvases 6 and 7' },
];
const INV_LABEL = { oneoff: 'One-off cost', p1: 'Phase 1 tranche', p2: 'Phase 2 tranche', run: 'Run cost per year', guard: 'Guardrail and change cost' };

const MONEY_ROWS = [
  { id: 'extra', label: 'Extra units a year', sub: 'volume x change, divided by 100 (or 1)' },
  { id: 'value', label: 'Value per year', sub: 'extra units x value per unit' },
];

const STORY_ROWS = [
  { id: 'growth', label: 'Growth story', sub: 'the Business row', from: 'business' },
  { id: 'efficiency', label: 'Efficiency story', sub: 'the AI and efficiency row', from: 'efficiency' },
];

const PER_OPTIONS = [
  { v: '100', label: '100', hint: 'per cent or percentage points' },
  { v: '1', label: '1', hint: 'a number per unit of volume' },
];

const STRENGTH = { M: 2, E: 1, G: 0 };

const MENU_HEAD = ['Value row', 'What it holds', 'Measures to choose from', 'Watch for'];
const MENU = [
  [
    'Customer',
    'One experience indicator: what the customer feels',
    'Customer Effort Score; satisfaction after the journey (CSAT); Net Promoter Score; repeat contacts for the same issue; time to complete the journey, as the customer counts it',
    'Pick one indicator and say which sponsor KPI you believe it moves. If nobody has tested that link, tag it G.',
  ],
  [
    'Business (the growth story)',
    'What grows',
    'Applications or orders completed, as a share of those started; conversion; revenue per customer; retention; second-product take-up',
    'The money line usually values this row. Use a measure you can multiply by a volume.',
  ],
  [
    'AI and efficiency (the efficiency story)',
    'Time and cost to serve',
    'Minutes of handling or re-keying per case; cost per case or per resolution; share resolved at first contact; share handled without a handover',
    'Time saved is not money saved. Say where the released time or cost goes.',
  ],
  [
    'Trust and Human (a guardrail measure)',
    'What must not get worse',
    'Complaints that name the AI step, per 1,000 cases; errors corrected by customers; share of handover requests that reach a person; decisions reversed on review',
    'Write it as a ceiling or a floor, not as a gain. "Human" here means the customer\'s access to a person.',
  ],
];

const HAND_HEAD = ['Step', 'Rule'];
const HAND_RULES = [
  ['Half the low case', 'Half the low case = the low change divided by 2. A ceiling has no half-the-low-case figure.'],
  ['Extra units a year', 'Extra units = volume per year x change, divided by 100 for per cent or percentage points (by 1 for a number per unit).'],
  ['Value per year', 'Value per year = extra units x value per unit. The canvas shows it in millions: 720000 reads as 0.72M.'],
  ['Simple payback', 'Payback in years = one-off midpoint divided by (value a year minus run-cost midpoint). It does not add the contingency.'],
  ['Does not pay back', 'If the value a year does not beat the run-cost midpoint, nothing is left to pay back the one-off cost. Write "does not pay back".'],
  ['Tags', 'M from a system you can show. E an estimate with a stated basis. G a guess. E and G go to the Evidence Gaps list.'],
];

// ---------------------------------------------------------------- local helpers
const factsOf = (up, n) => (up && up[n] && up[n].d && up[n].d.facts) || {};

const baselineLine = (b) => {
  const val = b.value ? `${b.value}${b.unit ? ` ${b.unit}` : ''}` : 'not yet known';
  return `${b.measure}: ${val}${b.tag ? `, tag ${b.tag}` : ''}`;
};

// the workbook lower-cases the first letter of the top pick unless it starts like an acronym ("AI ...")
const lowerFirst = (s) => (s.length < 2 || s[1] === s[1].toLowerCase() ? s.charAt(0).toLowerCase() + s.slice(1) : s);

// money for display: 720000 -> "S$0.72M", 200000 -> "S$0.2M", 1000000 -> "S$1.0M"; small amounts are shown in full
function money(x, cur) {
  if (x === null || x === undefined || Number.isNaN(x)) return '';
  const a = Math.abs(x);
  if (a !== 0 && a < 50000) return `${cur}${thousands(x)}`;
  const dp = Math.abs(x / 1e5 - Math.round(x / 1e5)) < 1e-9 ? 1 : 2;
  return moneyM(x, cur, dp);
}

const rangeOf = (lo, hi, cur) => (lo !== null && hi !== null ? `${money(lo, cur)} to ${money(hi, cur)}` : '');

const units = (x) => {
  if (x === null) return '';
  return Math.abs(x) >= 100 ? thousands(x, 0) : String(roundHalfUp(x, 2));
};

// "0.5 to 1.0 points lower", "30% to 50% lower", "a ceiling of 0 to 2 per 1,000 applications"
function changeText(r) {
  if (r.low === null || r.high === null) return '';
  if (r.direction === 'ceiling') return `a ceiling of ${r.lowText} to ${r.highText}${r.unit ? ` ${r.unit}` : ''}`;
  const pct = r.unit === '%';
  const tail = r.unit ? (pct ? '%' : ` ${r.unit}`) : '';
  return `${r.lowText}${pct ? '%' : ''} to ${r.highText}${tail}${r.direction ? ` ${r.direction}` : ''}`;
}

const isNoKpi = (s) => /^none\b/i.test(String(s || '').trim());

// ---------------------------------------------------------------- NorthStar (master spec section 6; printed canvas; fixture)
const NORTHSTAR = {
  ...T(
    'vr',
    [
      {
        measure: 'Customer Effort Score at account opening (7-point, lower is better)',
        baseline: 'Not yet measured',
        source: 'A 7-point survey after opening; no owner named yet',
        tag: 'G',
        direction: 'lower',
        low: '0.5',
        high: '1.0',
        unit: 'points',
        basis: 'Pre-fill removes the repeated step. The link to retention is untested',
        know: 'A 3-question survey, weekly',
        kpi: '12-month customer retention (link untested)',
      },
      {
        measure: 'Applications completed as a share of applications started',
        baseline: 'Not yet measured',
        source: 'Percentage of applications; application log; no owner named yet',
        tag: 'G',
        direction: 'higher',
        low: '5',
        high: '12',
        unit: 'percentage points',
        basis: 'The journey breaks at the repeated step; the size of the effect is assumed',
        know: 'Application log, weekly',
        kpi: '12-month customer retention',
      },
      {
        measure: 'Branch re-keying minutes per application',
        baseline: 'Not yet measured',
        source: 'Minutes; branch system time stamps; no owner named yet',
        tag: 'G',
        direction: 'lower',
        low: '30',
        high: '50',
        unit: '%',
        basis: 'Pre-fill removes the re-keying of what the bank already holds; the size is assumed',
        know: 'System time stamps, monthly',
        kpi: KPI_NONE,
      },
      {
        measure: 'Complaints citing wrong pre-filled data per 1,000 applications',
        baseline: 'Not tracked today',
        source: 'Per 1,000 applications; complaint log; no owner named yet',
        tag: 'G',
        direction: 'ceiling',
        low: '0',
        high: '2',
        unit: 'per 1,000 applications',
        basis: 'A guardrail: the customer can correct or decline the pre-filled data',
        know: 'Complaint log, monthly',
        kpi: 'Complaint resolution time',
      },
    ],
    ROW_IDS,
  ),
  'm.row': 'Business',
  'm.cur': 'S$',
  'm.per': '100',
  'm.volume': '240000',
  'm.volume_what': 'applications started a year (from 20,000 a month)',
  'm.volume_tag': 'E',
  'm.unit': '60',
  'm.unit_what': 'first-year margin per opened account',
  'm.unit_tag': 'G',
  ...T(
    'inv',
    [
      { tag: 'E', low: '600000', high: '1000000', basis: 'Over Phases 1 and 2' },
      { low: '200000', high: '300000', basis: 'Phase 1: mobilise and prove' },
      { low: '400000', high: '700000', basis: 'Phase 2: build and stabilise' },
      { tag: 'E', low: '150000', high: '250000', basis: 'From Phase 2' },
      { tag: 'E', low: '80000', high: '120000', basis: 'Set after Canvases 6 and 7; it sits inside the Phase 2 tranche' },
    ],
    INV_ROWS.map((r) => r.id),
  ),
  'ttv.tag': 'E',
  'ttv.low': '4',
  'ttv.high': '6',
  'ttv.basis': 'One region, one team',
  'cont.tag': 'E',
  'cont.pct': '25',
  'cont.holder': 'CFO',
  'assume.tag': 'G',
  'assume.text': 'The completion baseline is already high, leaving little to gain',
  'two.cfo': 'Payback inside three years. At half the low case the full build does not pay back in that time, so fund Phase 1 only: it buys the measured baseline',
  'story.for': 'customers opening an account',
  'story.will': 'cut the effort of applying and lift completed applications',
  'story.row': 'Customer',
  'story.kpi': '12-month customer retention',
  'story.readout':
    'For customers opening an account, pre-fill and document reading will cut the effort of applying and lift completed applications, ' +
    'measured by Customer Effort Score and completion, in support of 12-month retention.',
  'story.check3': 'Yes',
  'so.reviewer': 'CFO',
  'so.objection': 'None of your four baselines is measured. What could I verify from a system today?',
  'so.evidence': 'All four are tagged G and are Phase 1 measurement tasks: Canvas 4 tags, Evidence Gaps list',
  'so.owner': 'Head of Retail Banking',
  'so.status': 'Open',
  'so.star': 'Yes',
};

// ---------------------------------------------------------------- the definition
export default makeDef(4, {
  short: 'Value case',
  purpose: 'Put a number on what the top pick is worth and what it costs, and find out whether the CFO would still fund Phase 1 if the benefit were half your low case.',
  howTo: [
    'Four rows value the top pick: customer, growth, efficiency and trust. Give each row one measure, a baseline, a tag and a low and high change. ' +
      'No baseline, no benefit: a Guess is allowed, and it becomes a Phase 1 measurement task.',
    'The money line turns one row into money a year: volume x change x value per unit. ' +
      'It shows a low case, a high case and half the low case. Type whole amounts, for example 240000.',
    'The investment side gives a cost range and a contingency, then two payback figures. ' +
      'The CFO\'s third question is the second figure: at half the low case, do we still fund Phase 1?',
    'The Two-Story Test needs a growth story and an efficiency story. Everything you tag E or G goes to the Evidence Gaps list.',
  ],
  results: [
    { id: 'm', label: 'Tagged M', rule: 'Count the value rows whose baseline is tagged M.' },
    { id: 'e', label: 'Tagged E', rule: 'Count the value rows whose baseline is tagged E.' },
    { id: 'g', label: 'Tagged G', rule: 'Count the value rows whose baseline is tagged G. Each one becomes a Phase 1 measurement task.' },
    { id: 'nokpi', label: 'Rows with no sponsor KPI', rule: 'Count the rows whose linked KPI reads "None the sponsor owns yet".' },
    { id: 'weakest', label: 'Weakest value row (by its tag)', rule: 'G is weaker than E, and E is weaker than M. Name the rows with the weakest tag.' },
  ],
  steps: [
    // ------------------------------------------------------------ Step 1
    {
      hint: 'Copy the top pick, the journey and the sponsor KPIs. In the app they are pulled for you: if one is wrong, change it on its own canvas.',
      blocks: [
        pull('p1', 'Pulled from earlier canvases', [
          { id: 'top', label: 'Top pick', ref: 'Canvas 3', no: 3, get: (up) => factsOf(up, 3).topPick?.name || null },
          { id: 'journey', label: 'Journey', ref: 'Canvas 2', no: 2, get: (up) => factsOf(up, 2).journey || null },
          {
            id: 'flag',
            label: 'Guardrail flag on the top pick?',
            ref: 'Canvas 3',
            no: 3,
            get: (up) => {
              const t = factsOf(up, 3).topPick;
              if (!t) return null;
              return t.flag ? 'Yes: Canvas 6 tests it first. Price the guardrails in Step 4' : 'No';
            },
          },
          { id: 'sponsor', label: 'Sponsor', ref: 'Case Card C', no: 0, get: (up) => factsOf(up, 0).sponsor || null },
          {
            id: 'kpis',
            label: 'Sponsor KPIs',
            ref: 'Case Card C',
            no: 0,
            get: (up) => {
              const f = factsOf(up, 0);
              const list = [f.kpi1, f.kpi2].filter(Boolean);
              return list.length ? list.join('\n') : null;
            },
            note: 'Link each value row to one of these in Step 2.',
          },
          {
            id: 'baselines',
            label: 'Baselines',
            ref: 'Case Card D',
            no: 0,
            get: (up) => {
              const list = (factsOf(up, 0).baselines || []).filter((b) => b.measure);
              return list.length ? list.map(baselineLine).join('\n') : null;
            },
            note: 'Your baseline numbers and their tags. A baseline you already have is the first thing the CFO will ask for.',
          },
        ]),
      ],
    },
    // ------------------------------------------------------------ Step 2
    {
      hint:
        'Fill one measure per row. Give the size of the change as positive numbers, low and high, and pick the direction. ' +
        'No baseline, no benefit: a Guess is allowed, and it becomes a Phase 1 measurement task.',
      blocks: [
        table('vr', {
          title: 'Value rows',
          rows: { fixed: ROWS },
          pdf: 'cards',
          columns: [
            text('measure', 'Measure (one only)', {
              required: true,
              w: 4,
              guide: G(
                'Name the one measure a system could show for this row. One only: a row with three measures has no measure.',
                'Business row: Applications completed as a share of applications started.',
                undefined,
                'A goal is not a measure. "Improve the experience" cannot be read off a system.',
              ),
            }),
            text('baseline', 'Baseline now', {
              required: true,
              w: 2,
              guide: G(
                'Write today\'s value of the measure. If you do not have it, write "Not yet measured" and say where it lives in the next box.',
                'Not yet measured',
                'No baseline, no benefit.',
                'Do not invent a baseline to make the gain look real. A Guess is allowed: tag it G and it becomes a Phase 1 measurement task.',
              ),
            }),
            text('source', 'Unit, source and owner of the baseline', {
              w: 2,
              guide: G(
                'The unit, the system the number comes from, and who owns it. Write "no owner named yet" if that is the case.',
                'Percentage of applications; application log; no owner named yet',
              ),
            }),
            tag('tag', 'Tag', {
              required: true,
              w: 1,
              guide: G(
                'Tag the baseline: M from a report or system you can show, E an estimate with a stated basis, G a guess.',
                'G, because the baseline is not yet measured.',
                'A baseline tagged E or G goes to the Evidence Gaps list and becomes a Phase 1 measurement task.',
                'The tag is about the baseline, not the gain. A gain is a forecast until Phase 1 measures it.',
              ),
            }),
            pick('direction', 'Direction', OPT.direction, {
              required: true,
              w: 1,
              guide: G(
                'Pick which way the measure should move. Higher or lower is a gain. A ceiling is a limit the measure must stay under.',
                'Higher for completion; lower for effort and minutes; ceiling for the guardrail.',
                'A ceiling is a guardrail, not a gain, so it has no half-the-low-case figure.',
              ),
            }),
            num('low', 'Low', {
              required: true,
              w: 1,
              min: 0,
              guide: G(
                'The smallest change you would defend, as a positive number.',
                '5 (percentage points) on the Business row.',
                'Low must be smaller than high.',
                'Type the size of the change, not the new level, and no minus sign: the direction says which way.',
              ),
            }),
            num('high', 'High', {
              required: true,
              w: 1,
              min: 0,
              guide: G(
                'The largest change you would defend, as a positive number.',
                '12 (percentage points) on the Business row.',
                undefined,
                'A wide range with no reason is a guess. Say why in the Basis box.',
              ),
            }),
            text('unit', 'Unit of the change', {
              w: 2,
              guide: G(
                'What the change is counted in: points, percentage points, minutes. Type % when the change is relative, as in 30% to 50% lower.',
                'percentage points',
                undefined,
                'Percentage points and per cent differ. Moving from 60 per cent to 65 per cent is 5 percentage points, or 8.3 per cent more.',
              ),
            }),
            calc('half', 'Half the low case', {
              w: 2,
              rule: 'the low change divided by 2; blank for a ceiling',
              guide: G(
                'Worked out for you. It is the case the CFO will ask about: the benefit if the low case is only half right.',
                '2.5 on the Business row.',
                'Half the low case = low divided by 2.',
              ),
            }),
            text('basis', 'Basis: why this range', {
              required: true,
              w: 4,
              guide: G(
                'Say why the range is this wide, in one line. Say what you know and what you assume.',
                'The journey breaks at the repeated step; the size of the effect is assumed',
                undefined,
                '"Industry average" with no source is not a basis.',
              ),
            }),
            text('know', 'How we will know', {
              required: true,
              w: 2,
              guide: G(
                'The source, how often it is read, and what you compare against.',
                'Application log, weekly',
                undefined,
                'A measure nobody reads is not a measure. Name the source and the rhythm.',
              ),
            }),
            text('kpi', 'Linked sponsor KPI', {
              required: true,
              w: 2,
              placeholder: `A KPI from Case Card C, or: ${KPI_NONE}`,
              guide: G(
                'The sponsor KPI this row supports, written as on Case Card C. If the link is untested, say so in brackets. ' +
                  `If the sponsor owns no KPI for this row, write: ${KPI_NONE}.`,
                '12-month customer retention (link untested)',
                undefined,
                'Do not stretch a KPI to fit. A row with no sponsor KPI is a finding for the CFO, not a gap to fill.',
              ),
            }),
          ],
        }),
        reference('A measure menu for the four value rows', MENU_HEAD, MENU),
      ],
    },
    // ------------------------------------------------------------ Step 3
    {
      hint: 'Work out the money: volume x change x value per unit, in your own currency. Type whole amounts.',
      blocks: [
        fields([
          pick('m.row', 'Value row this line values', ROW_NAMES, {
            required: true,
            w: 2,
            default: 'Business',
            guide: G(
              'Pick the one value row the money line turns into money. It is usually the Business row, the growth story: you can multiply it by a volume.',
              'Business',
              undefined,
              'Value one row only. Adding two rows counts the same customer twice.',
            ),
          }),
          text('m.cur', 'Currency', {
            required: true,
            w: 1,
            default: 'S$',
            guide: G(
              'The currency symbol or code for every amount on this canvas.',
              'S$',
              undefined,
              'Type whole amounts in this currency in Steps 3 and 4, for example 240000, not 0.24M. The canvas shows the results in millions.',
            ),
          }),
          pick('m.per', 'The change is per', PER_OPTIONS, {
            required: true,
            w: 1,
            default: '100',
            guide: G(
              'Pick 100 when the change is in per cent or percentage points. Pick 1 when it is a number per unit of volume.',
              '100',
              '100 for per cent or percentage points; 1 for a number per unit of volume.',
            ),
          }),
          num('m.volume', 'Volume per year', {
            required: true,
            w: 1,
            min: 0,
            guide: G(
              'How many units go through the journey in a year.',
              '240000',
              'A volume of 20,000 a month is 240,000 a year.',
              'Use a year, not a month: the value is a yearly figure.',
            ),
          }),
          text('m.volume_what', 'What the volume counts', {
            required: true,
            w: 2,
            guide: G('Say what one unit is.', 'applications started a year (from 20,000 a month)'),
          }),
          tag('m.volume_tag', 'Tag for the volume', {
            required: true,
            w: 1,
            guide: G(
              'M if a report shows it; E if you estimated it and can say how; G if it is a guess.',
              'E',
              'E or G goes to the Evidence Gaps list.',
            ),
          }),
          num('m.unit', 'Value per unit', {
            required: true,
            w: 1,
            min: 0,
            guide: G(
              'What one extra unit is worth to the business, in your currency.',
              '60',
              undefined,
              'Say whether it is margin or revenue. NorthStar uses first-year margin.',
            ),
          }),
          text('m.unit_what', 'What the value counts', {
            required: true,
            w: 2,
            guide: G('Say what the value is for one unit.', 'first-year margin per opened account'),
          }),
          tag('m.unit_tag', 'Tag for the value', {
            required: true,
            w: 1,
            guide: G(
              'M if finance has the figure; E if you estimated it and can say how; G if it is a guess.',
              'G',
              'E or G goes to the Evidence Gaps list.',
            ),
          }),
          calc('m.change', 'The change on that row (from Step 2)', {
            w: 4,
            rule: 'copied from the value row you chose',
            guide: G('Worked out for you from Step 2. Check that it is the change you mean to value.', '5 to 12 percentage points higher'),
          }),
        ]),
        table('money', {
          title: 'The money line',
          rows: { fixed: MONEY_ROWS },
          pdf: 'table',
          columns: [
            calc('low', 'Low case', {
              w: 1,
              rule: 'the low change',
              guide: G('Worked out for you with the low change from Step 2.', '12,000 extra accounts; S$0.72M a year'),
            }),
            calc('high', 'High case', {
              w: 1,
              rule: 'the high change',
              guide: G('Worked out for you with the high change from Step 2.', '28,800 extra accounts; S$1.73M a year'),
            }),
            calc('half', 'Half the low case', {
              w: 1,
              rule: 'half of the low change',
              guide: G('Worked out for you with half the low change. It feeds the second payback figure.', '6,000 extra accounts; S$0.36M a year'),
            }),
          ],
        }),
        fields([
          calc('m.range', 'The range, as one line', {
            w: 4,
            rule: 'low case to high case, a year',
            guide: G('Worked out for you. This is the line to read out.', 'S$0.72M to S$1.73M a year'),
          }),
        ]),
        reference('Work it out by hand', HAND_HEAD, HAND_RULES),
      ],
    },
    // ------------------------------------------------------------ Step 4
    {
      hint:
        'Cost the investment side in the Step 3 currency. The CFO\'s third question is the second payback figure: ' +
        'at half the low case, do we still fund Phase 1?',
      blocks: [
        table('inv', {
          title: 'Investment',
          rows: { fixed: INV_ROWS },
          pdf: 'table',
          columns: [
            tag('tag', 'Tag', {
              required: ({ rowInfo }) => !(rowInfo && (rowInfo.id === 'p1' || rowInfo.id === 'p2')),
              w: 1,
              guide: G(
                'M from a quote or contract; E an estimate with a stated basis; G a guess. The two tranches take the tag of the one-off cost.',
                'E',
                'E or G goes to the Evidence Gaps list.',
              ),
            }),
            num('low', 'Low', {
              required: true,
              w: 1,
              min: 0,
              guide: G(
                'The low end of the range, as a whole amount in your currency.',
                '600000 for the one-off cost.',
                'The two tranches add up to the one-off cost. The guardrail and change cost sits inside it: if it does not, add it to the one-off cost.',
                'One figure with no range is not a cost estimate. Give a low and a high.',
              ),
            }),
            num('high', 'High', {
              required: true,
              w: 1,
              min: 0,
              guide: G(
                'The high end of the range, as a whole amount in your currency.',
                '1000000 for the one-off cost.',
                undefined,
                'Do not hide the contingency in this figure. Keep it in its own line below.',
              ),
            }),
            text('basis', 'Basis or note', {
              w: 2,
              guide: G(
                'Where the range comes from, or what it covers.',
                'Set after Canvases 6 and 7; it sits inside the Phase 2 tranche',
              ),
            }),
          ],
        }),
        fields([
          num('ttv.low', 'Time to first value: low, in months', {
            required: true,
            w: 1,
            min: 0,
            guide: G(
              'Months from the go-ahead to the first customers feeling the change. The low end.',
              '4',
              undefined,
              'First value is not payback. It is when the first customers feel the change.',
            ),
          }),
          num('ttv.high', 'Time to first value: high, in months', {
            required: true,
            w: 1,
            min: 0,
            guide: G('The high end of the same range.', '6'),
          }),
          tag('ttv.tag', 'Tag for the time to first value', {
            required: true,
            w: 1,
            guide: G('M if you have done it before; E if you estimated it and can say how; G if it is a guess.', 'E', 'E or G goes to the Evidence Gaps list.'),
          }),
          text('ttv.basis', 'Basis or note', {
            w: 1,
            guide: G('What the range assumes.', 'One region, one team'),
          }),
          num('cont.pct', 'Contingency, in per cent', {
            required: true,
            w: 1,
            unit: '%',
            min: 0,
            guide: G(
              'The share you add to the cost range for what you cannot yet see.',
              '25',
              undefined,
              '"Contingency is in the number" is not an answer. State the per cent and who holds it.',
            ),
          }),
          text('cont.holder', 'Who holds the contingency (a role)', {
            required: true,
            w: 2,
            guide: G('The role that holds it, not a person.', 'CFO'),
          }),
          tag('cont.tag', 'Tag for the contingency', {
            required: true,
            w: 1,
            guide: G('M if policy sets it; E if you estimated it and can say how; G if it is a guess.', 'E', 'E or G goes to the Evidence Gaps list.'),
          }),
          text('assume.text', 'The assumption that would hurt most', {
            required: true,
            w: 3,
            guide: G(
              'The one assumption that, if wrong, hurts the case most.',
              'The completion baseline is already high, leaving little to gain',
              undefined,
              'Name one thing that could be wrong, not a risk in general.',
            ),
          }),
          tag('assume.tag', 'Tag for the assumption', {
            required: true,
            w: 1,
            guide: G('How well you can back the assumption: M, E or G.', 'G', 'E or G goes to the Evidence Gaps list.'),
          }),
          calc('inv.payback_low', 'Simple payback, in years: at the low case', {
            w: 2,
            rule: 'one-off midpoint divided by (value a year minus run-cost midpoint)',
            guide: G('Worked out for you from the low case and the middle of each cost range.', '1.5 years'),
          }),
          calc('inv.payback_half', 'Simple payback, in years: at half the low case', {
            w: 2,
            rule: 'the same line, with the value at half the low case',
            guide: G(
              'Worked out for you. This is the CFO\'s third question: if the benefit is half, do we still fund Phase 1?',
              '5.0 years',
              'If the value does not beat the run cost it reads "does not pay back". That is an answer.',
            ),
          }),
        ]),
      ],
    },
    // ------------------------------------------------------------ Step 5
    {
      hint: 'Run the Two-Story Test (an AICX working rule): a growth story and an efficiency story must both be present. Then write the CFO\'s own bar.',
      blocks: [
        table('ts', {
          title: 'The Two-Story Test',
          rows: { fixed: STORY_ROWS.map(({ id, label, sub }) => ({ id, label, sub })) },
          pdf: 'table',
          columns: [
            calc('measure', 'Measure (Step 2)', {
              w: 2,
              rule: 'copied from the row',
              guide: G('Worked out for you from Step 2.', 'Applications completed as a share of applications started'),
            }),
            calc('baseline', 'Baseline', { w: 1, rule: 'copied from the row', guide: G('Worked out for you from Step 2.', 'Not yet measured') }),
            calc('tag', 'Tag', { w: 1, rule: 'copied from the row', guide: G('Worked out for you from Step 2.', 'G') }),
            calc('change', 'Change', { w: 2, rule: 'low to high, with its unit and direction', guide: G('Worked out for you from Step 2.', '5 to 12 percentage points higher') }),
            calc('present', 'Present?', {
              w: 1,
              rule: 'the row has a measure, a baseline, and a low and high',
              guide: G('Worked out for you. Yes when the row has a measure, a baseline and a range.', 'Yes'),
            }),
          ],
        }),
        fields([
          calc('two.both', 'Both stories present?', {
            w: 1,
            rule: 'Yes when both rows are present',
            guide: G('Worked out for you from the two rows above.', 'Yes', 'A business case needs a growth story and an efficiency story. One of them alone is half a case.'),
          }),
          calc('two.msg', 'What this means', {
            w: 3,
            rule: 'a plain message: what is present and what is missing',
            guide: G('Worked out for you. It names the story that is missing and what it needs.', 'Both stories are present.'),
          }),
          long('two.cfo', 'The CFO\'s own bar (ask, then write it)', {
            w: 4,
            rows: 3,
            guide: G(
              'Ask the CFO what they need to see before they fund it, then write it here. If you cannot ask yet, write what you believe it is.',
              'Payback inside three years. At half the low case the full build does not pay back in that time, so fund Phase 1 only: it buys the measured baseline',
              undefined,
              'Without the bar, a payback figure has no meaning. "Good returns" is not a bar.',
            ),
          }),
        ]),
      ],
    },
    // ------------------------------------------------------------ Step 6
    {
      hint: 'Write the value story as one sentence, then read it out. Frame: for [customers], [top pick] will [outcome], measured by [measure], in support of [sponsor KPI].',
      blocks: [
        fields([
          text('story.for', 'For (the customers on this journey)', {
            required: true,
            w: 2,
            guide: G('The customers on this journey, in a few words.', 'customers opening an account'),
          }),
          text('story.will', 'will (the outcome, in plain words)', {
            required: true,
            w: 2,
            guide: G(
              'The outcome, with a verb: cut, lift, reduce, shorten.',
              'cut the effort of applying and lift completed applications',
              undefined,
              'Claim only what a value row on this canvas shows.',
            ),
          }),
          pick('story.row', 'measured by (choose the value row)', ROW_NAMES, {
            required: true,
            w: 2,
            guide: G('Pick the value row whose measure backs the outcome. Its measure is filled in below.', 'Customer'),
          }),
          text('story.kpi', 'in support of (a sponsor KPI)', {
            required: true,
            w: 2,
            placeholder: `A KPI from Case Card C, or: ${KPI_NONE}`,
            guide: G(
              'A KPI your sponsor already answers for, written as on Case Card C.',
              '12-month customer retention',
              undefined,
              'A story that supports no sponsor KPI is a story the sponsor will not carry.',
            ),
          }),
          calc('story.measure', 'The measure of that row (Step 2)', {
            w: 4,
            rule: 'copied from the value row you chose',
            guide: G('Worked out for you from Step 2.', 'Customer Effort Score at account opening (7-point, lower is better)'),
          }),
          calc('story.assembled', 'The sentence, assembled', {
            w: 4,
            rule: 'for ..., the top pick will ..., measured by ..., in support of ...',
            guide: G(
              'Worked out for you from the boxes above. Use it as a first draft.',
              'For customers opening an account, pre-fill and document reading will cut the effort of applying and lift completed applications, ' +
                'measured by Customer Effort Score at account opening (7-point, lower is better), in support of 12-month customer retention.',
            ),
          }),
          long('story.readout', 'Your read-out version (edit freely)', {
            required: true,
            w: 4,
            rows: 3,
            guide: G(
              'Write the sentence as you will say it aloud. One sentence, no number the canvas does not show.',
              'For customers opening an account, pre-fill and document reading will cut the effort of applying and lift completed applications, ' +
                'measured by Customer Effort Score and completion, in support of 12-month retention.',
              undefined,
              'NorthStar\'s sentence carries no number because all four baselines are still guesses. When your range is measured, add how much and by when.',
            ),
          }),
          calc('story.check1', 'Read-out check 1: it names a KPI the sponsor answers for', {
            w: 4,
            rule: 'the KPI box holds a KPI, not "None the sponsor owns yet"',
            guide: G('Worked out for you from the KPI box.', 'Names a sponsor KPI'),
          }),
          calc('story.check2', 'Read-out check 2: its outcome is a claim here, with a baseline and a tag', {
            w: 4,
            rule: 'the chosen row has a baseline and a tag',
            guide: G('Worked out for you from the value row you chose.', 'That row has a baseline and a tag (G)'),
          }),
          pick('story.check3', 'Read-out check 3: it uses no number this canvas does not show (tick Yes)', OPT.yes, {
            w: 4,
            guide: G(
              'Read your sentence against the canvas. Tick Yes when every number in it is shown here.',
              'Yes',
              undefined,
              'A number from memory, with no cell behind it, is the first thing the CFO challenges.',
            ),
          }),
        ]),
      ],
    },
  ],

  // ---------------------------------------------------------------- derive
  derive(v, up) {
    const f0 = factsOf(up, 0);
    const f2 = factsOf(up, 2);
    const f3 = factsOf(up, 3);
    const top = f3.topPick || null;
    const cur = S(v, 'm.cur') || 'S$';

    // the four value rows
    const vr = ROWS.map((r) => {
      const k = (c) => `vr.${r.id}.${c}`;
      const low = N(v, k('low'));
      const high = N(v, k('high'));
      const row = {
        id: r.id,
        label: r.label,
        measure: S(v, k('measure')),
        baseline: S(v, k('baseline')),
        source: S(v, k('source')),
        tag: S(v, k('tag')),
        direction: S(v, k('direction')),
        lowText: S(v, k('low')),
        highText: S(v, k('high')),
        low,
        high,
        unit: S(v, k('unit')),
        basis: S(v, k('basis')),
        know: S(v, k('know')),
        kpi: S(v, k('kpi')),
      };
      row.half = low !== null && row.direction !== 'ceiling' ? roundHalfUp(low / 2, 3) : null;
      row.change = changeText(row);
      row.present = !!(row.measure && row.baseline && low !== null && high !== null);
      return row;
    });
    const byId = (id) => vr.find((r) => r.id === id);

    const calcOut = {};
    vr.forEach((r) => {
      calcOut[`vr.${r.id}.half`] = r.half === null ? '' : String(r.half);
    });

    // the money line
    const volume = N(v, 'm.volume');
    const per = N(v, 'm.per');
    const unitVal = N(v, 'm.unit');
    const valued = vr.find((r) => r.label === S(v, 'm.row')) || null;
    const line = (chg) => {
      if (chg === null || volume === null || per === null || per === 0) return { units: null, value: null };
      const u = roundHalfUp((volume * chg) / per, 6);
      const value = unitVal === null ? null : roundHalfUp(u * unitVal, 4);
      return { units: u, value };
    };
    const lowL = line(valued ? valued.low : null);
    const highL = line(valued ? valued.high : null);
    const halfL = line(valued && valued.low !== null ? valued.low / 2 : null);
    calcOut['m.change'] = valued ? valued.change : '';
    calcOut['money.extra.low'] = units(lowL.units);
    calcOut['money.extra.high'] = units(highL.units);
    calcOut['money.extra.half'] = units(halfL.units);
    calcOut['money.value.low'] = money(lowL.value, cur);
    calcOut['money.value.high'] = money(highL.value, cur);
    calcOut['money.value.half'] = money(halfL.value, cur);
    const rangeText = lowL.value !== null && highL.value !== null ? `${money(lowL.value, cur)} to ${money(highL.value, cur)} a year` : '';
    calcOut['m.range'] = rangeText;

    // the investment side
    const inv = {};
    INV_ROWS.forEach((r) => {
      inv[r.id] = { low: N(v, `inv.${r.id}.low`), high: N(v, `inv.${r.id}.high`), tag: S(v, `inv.${r.id}.tag`), basis: S(v, `inv.${r.id}.basis`) };
    });
    const ttvLow = N(v, 'ttv.low');
    const ttvHigh = N(v, 'ttv.high');
    const timeToValue = ttvLow !== null && ttvHigh !== null ? (ttvLow === ttvHigh ? `${ttvLow} months` : `${ttvLow} to ${ttvHigh} months`) : '';
    const mid = (a) => (a.low !== null && a.high !== null ? (a.low + a.high) / 2 : null);
    const oneMid = mid(inv.oneoff);
    const runMid = mid(inv.run);
    const payback = (value) => {
      if (value === null || oneMid === null || runMid === null) return '';
      const left = value - runMid;
      return left <= 0 ? 'does not pay back' : `${fix(oneMid / left, 1)} years`;
    };
    calcOut['inv.payback_low'] = payback(lowL.value);
    calcOut['inv.payback_half'] = payback(halfL.value);
    const contPct = N(v, 'cont.pct');

    // the Two-Story Test
    const story = {};
    STORY_ROWS.forEach((s) => {
      const r = byId(s.from);
      const any = !!(r.measure || r.baseline || r.low !== null || r.high !== null);
      story[s.id] = r;
      calcOut[`ts.${s.id}.measure`] = r.measure;
      calcOut[`ts.${s.id}.baseline`] = r.baseline;
      calcOut[`ts.${s.id}.tag`] = r.tag;
      calcOut[`ts.${s.id}.change`] = r.change;
      calcOut[`ts.${s.id}.present`] = r.present ? 'Yes' : any ? 'Not yet' : '';
    });
    const anyStory = STORY_ROWS.some((s) => story[s.id].measure || story[s.id].baseline || story[s.id].low !== null || story[s.id].high !== null);
    const bothPresent = STORY_ROWS.every((s) => story[s.id].present);
    const gaps = STORY_ROWS.filter((s) => !story[s.id].present).map((s) => {
      const r = story[s.id];
      const need = [];
      if (!r.measure) need.push('a measure');
      if (!r.baseline) need.push('a baseline');
      if (r.low === null || r.high === null) need.push('a low and a high');
      return `${s.label} (${s.sub}): add ${need.join(', ')}.`;
    });
    calcOut['two.both'] = !anyStory ? '' : bothPresent ? 'Yes' : 'No';
    calcOut['two.msg'] = !anyStory
      ? ''
      : bothPresent
        ? 'Both stories are present: growth (the Business row) and efficiency (the AI and efficiency row).'
        : `Not both stories yet. ${gaps.join(' ')}`;

    // the value story
    const sRow = vr.find((r) => r.label === S(v, 'story.row')) || null;
    const sFor = S(v, 'story.for');
    const sWill = S(v, 'story.will');
    const sKpi = S(v, 'story.kpi');
    const topName = top ? top.name : '';
    const sMeasure = (sRow && sRow.measure) || '[a measure]';
    const assembled =
      sFor && topName && sWill ? `For ${sFor}, ${lowerFirst(topName)} will ${sWill}, measured by ${sMeasure}, in support of ${sKpi || '[a sponsor KPI]'}.` : '';
    calcOut['story.measure'] = sRow ? sRow.measure : '';
    calcOut['story.assembled'] = assembled;
    calcOut['story.check1'] = !sKpi ? '' : isNoKpi(sKpi) ? 'No sponsor KPI yet: raise it with the CFO' : 'Names a sponsor KPI';
    calcOut['story.check2'] = !sRow
      ? ''
      : sRow.baseline && sRow.tag
        ? `That row has a baseline and a tag (${sRow.tag})`
        : 'That row has no baseline or no tag';
    const readout = S(v, 'story.readout');

    // the result tiles
    const tags = { M: 0, E: 0, G: 0 };
    const tagged = vr.filter((r) => Object.prototype.hasOwnProperty.call(STRENGTH, r.tag));
    tagged.forEach((r) => {
      tags[r.tag] += 1;
    });
    const kpiRows = vr.filter((r) => r.kpi);
    let weakest = '';
    if (tagged.length) {
      const strengths = tagged.map((r) => STRENGTH[r.tag]);
      const lo = Math.min(...strengths);
      const hi = Math.max(...strengths);
      const word = ['G', 'E', 'M'][lo];
      if (lo === hi) weakest = tagged.length === 1 ? `The one tagged row is ${word}` : `All ${tagged.length} tagged rows are ${word}`;
      else weakest = tagged.filter((r) => STRENGTH[r.tag] === lo).map((r) => r.label).join(', ');
    }
    const results = {
      m: tagged.length ? String(tags.M) : '',
      e: tagged.length ? String(tags.E) : '',
      g: tagged.length ? String(tags.G) : '',
      nokpi: kpiRows.length ? String(kpiRows.filter((r) => isNoKpi(r.kpi)).length) : '',
      weakest,
    };

    // evidence: every number tagged E or G
    const evidence = [];
    const addGap = (item, t, where) => {
      if (t === 'E' || t === 'G') evidence.push(gap(item, t, where));
    };
    vr.forEach((r) => addGap(`Baseline: ${r.measure || r.label}${r.baseline ? ` (${r.baseline})` : ''}`, r.tag, `Canvas 4, ${r.label} row`));
    addGap(`Volume per year: ${volume !== null ? thousands(volume) : ''} ${S(v, 'm.volume_what')}`.trim(), S(v, 'm.volume_tag'), 'Canvas 4, money line, volume');
    addGap(`Value per unit: ${cur}${S(v, 'm.unit')} ${S(v, 'm.unit_what')}`.trim(), S(v, 'm.unit_tag'), 'Canvas 4, money line, value per unit');
    ['oneoff', 'run', 'guard'].forEach((id) => {
      const a = inv[id];
      const rng = rangeOf(a.low, a.high, cur);
      addGap(`${INV_LABEL[id]}${rng ? `: ${rng}` : ': no range yet'}`, a.tag, `Canvas 4, Step 4, ${INV_LABEL[id].toLowerCase()}`);
    });
    addGap(`Time to first value${timeToValue ? `: ${timeToValue}` : ': no range yet'}`, S(v, 'ttv.tag'), 'Canvas 4, Step 4, time to first value');
    const holder = S(v, 'cont.holder');
    addGap(`Contingency: ${contPct !== null ? `${contPct}%` : 'not set'}${holder ? `, held by ${holder}` : ''}`, S(v, 'cont.tag'), 'Canvas 4, Step 4, contingency');
    addGap(`Assumption: ${S(v, 'assume.text') || 'not written yet'}`, S(v, 'assume.tag'), 'Canvas 4, Step 4, the assumption that would hurt most');

    // warnings and blockers
    const warnings = [];
    const blockers = [];
    vr.forEach((r) => {
      if (r.low !== null && r.high !== null) {
        if (r.low > r.high) blockers.push({ step: 's2', text: `${r.label} row: low is larger than high. Swap them.` });
        else if (r.low === r.high) warnings.push({ step: 's2', text: `${r.label} row: give a range. Low and high are the same.` });
      }
      if (r.measure && !r.baseline) warnings.push({ step: 's2', text: `${r.label} row: no baseline, no benefit. Write it, or where it lives.` });
      if (r.measure && !r.kpi) warnings.push({ step: 's2', text: `${r.label} row: link a sponsor KPI, or write "${KPI_NONE}".` });
    });
    if (valued && valued.direction === 'ceiling') {
      warnings.push({ step: 's3', text: `The ${valued.label} row is a ceiling, not a gain. Value a row whose measure improves.` });
    }
    if (valued && valued.low !== null && valued.high !== null && !valued.change) {
      warnings.push({ step: 's3', text: 'The row you are valuing has no low and high yet. Fill them in Step 2.' });
    }
    const checkRange = (id) => {
      const a = inv[id];
      if (a.low !== null && a.high !== null && a.low > a.high) blockers.push({ step: 's4', text: `${INV_LABEL[id]}: low is larger than high. Swap them.` });
    };
    INV_ROWS.forEach((r) => checkRange(r.id));
    if (ttvLow !== null && ttvHigh !== null && ttvLow > ttvHigh) blockers.push({ step: 's4', text: 'Time to first value: low is larger than high. Swap them.' });
    const allSix = [inv.oneoff.low, inv.oneoff.high, inv.p1.low, inv.p1.high, inv.p2.low, inv.p2.high].every((x) => x !== null);
    if (allSix && (inv.p1.low + inv.p2.low !== inv.oneoff.low || inv.p1.high + inv.p2.high !== inv.oneoff.high)) {
      warnings.push({
        step: 's4',
        text:
          `The two tranches add to ${rangeOf(inv.p1.low + inv.p2.low, inv.p1.high + inv.p2.high, cur)}, ` +
          `but the one-off cost is ${rangeOf(inv.oneoff.low, inv.oneoff.high, cur)}. Make them agree, or say why in the basis.`,
      });
    }
    if (top && top.flag && inv.guard.low === null && inv.guard.high === null) {
      warnings.push({ step: 's4', text: 'The top pick carries a guardrail flag. Put a placeholder in the guardrail and change cost, then return after Canvases 6 and 7.' });
    }
    if (calcOut['inv.payback_half'] === 'does not pay back') {
      warnings.push({ step: 's4', text: 'At half the low case the value does not cover the run cost. Say so, and ask what a smaller first ask, such as Phase 1 only, would buy.' });
    }
    if (anyStory && !bothPresent) warnings.push({ step: 's5', text: 'The Two-Story Test fails: a business case needs both a growth story and an efficiency story.' });
    if (sKpi && !isNoKpi(sKpi) && (f0.kpi1 || f0.kpi2)) {
      const kpis = [f0.kpi1, f0.kpi2].filter(Boolean).map((k) => k.toLowerCase());
      const t = sKpi.toLowerCase();
      if (!kpis.some((k) => k.includes(t) || t.includes(k))) {
        warnings.push({ step: 's6', text: 'The KPI in your value story is not one of the two sponsor KPIs on Case Card C. Check that the sponsor answers for it.' });
      }
    }
    if (readout && /\d/.test(readout) && S(v, 'story.check3') !== 'Yes') {
      warnings.push({ step: 's6', text: 'Your sentence holds a number. Check that this canvas shows it, then tick check 3.' });
    }

    // facts: what Canvases 5, 7, 8 and 9 read
    const rowFacts = Object.fromEntries(
      vr.map((r) => [
        r.id,
        {
          measure: r.measure,
          baseline: r.baseline,
          source: r.source,
          tag: r.tag,
          direction: r.direction,
          low: r.low,
          high: r.high,
          half: r.half,
          unit: r.unit,
          change: r.change,
          basis: r.basis,
          know: r.know,
          kpi: r.kpi,
        },
      ]),
    );
    const whole = (x) => (x === null ? null : roundHalfUp(x, 0));
    const facts = {
      cur,
      topPick: topName,
      guardrailFlag: !!(top && top.flag),
      journey: f2.journey || '',
      kpis: [f0.kpi1, f0.kpi2].filter(Boolean),
      rows: rowFacts,
      money: {
        volume,
        valuePer: unitVal,
        valuedRow: valued ? valued.id : '',
        lowPts: valued ? valued.low : null,
        highPts: valued ? valued.high : null,
        lowAccounts: whole(lowL.units),
        highAccounts: whole(highL.units),
        lowValue: whole(lowL.value),
        highValue: whole(highL.value),
        halfLowAccounts: whole(halfL.units),
        halfLowValue: whole(halfL.value),
        rangeText,
      },
      invest: {
        oneOffLow: inv.oneoff.low,
        oneOffHigh: inv.oneoff.high,
        p1Low: inv.p1.low,
        p1High: inv.p1.high,
        p2Low: inv.p2.low,
        p2High: inv.p2.high,
        runLow: inv.run.low,
        runHigh: inv.run.high,
        guardLow: inv.guard.low,
        guardHigh: inv.guard.high,
        contingencyPct: contPct,
        contingencyHolder: S(v, 'cont.holder'),
        timeToValue,
        p1Text: rangeOf(inv.p1.low, inv.p1.high, cur),
        p2Text: rangeOf(inv.p2.low, inv.p2.high, cur),
        paybackLow: calcOut['inv.payback_low'],
        paybackHalf: calcOut['inv.payback_half'],
      },
      tags,
      bothStories: anyStory ? bothPresent : null,
      cfoBar: S(v, 'two.cfo'),
      assumption: S(v, 'assume.text'),
      valueStory: readout || assembled,
    };

    return { calc: calcOut, results, facts, evidence, warnings, blockers };
  },
  NORTHSTAR,
});
